import type { FC } from 'react'
import type { PropsWithChildren } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import type { ProviderResult } from '@alexanderathoodly/data-models'
import { useTranslation } from 'react-i18next'
import { getImgProxyUrl, SIZE_1200 } from '@yta/image-proxy'
import { useTranslationKey } from '~/utils/translations'
import { useAlternateLinks } from '~/utils/alternatelinks'

export const CoworkingOperatorHelmet: FC<
	PropsWithChildren<{
		provider: ProviderResult
	}>
> = ({ provider, children }) => {
	const { i18n } = useTranslation()
	const title = provider.name

	const match = useRouteMatch()
	const translationKey = useTranslationKey(match.url, 'url')
	const alternateLinks = useAlternateLinks(translationKey)
	return (
		<Helmet>
			<title key="title">{title}</title>
			<meta
				name="description"
				content={
					i18n.language != 'en'
						? provider.operator_page_description_about
						: provider.operator_page_description_about_en
				}
			/>
			<meta name="robots" content={'index, follow'} />
			{provider.locations[0]?.images[0] && (
				<meta
					property="og:image"
					content={getImgProxyUrl(provider.locations[0].images[0], SIZE_1200)}
				/>
			)}
			<meta property="og:image:width" content={SIZE_1200.width.toString()} />
			<meta property="og:image:height" content={SIZE_1200.height.toString()} />
			{alternateLinks &&
				alternateLinks.map((link) => (
					<link
						key={link.url}
						rel="alternate"
						hrefLang={link.lng}
						href={link.url}
					/>
				))}
			{alternateLinks && (
				<link
					rel="alternate"
					hrefLang="x-default"
					href={alternateLinks.find((link) => link.lng == 'sv')!.url}
				/>
			)}
			{alternateLinks && (
				<link
					rel="canonical"
					href={alternateLinks.find((link) => link.lng == i18n.language)!.url}
				/>
			)}
			{children}
		</Helmet>
	)
}
